import React, { useState, useEffect } from 'react';
import HolsterVisualizer from '../HolsterVisualizer';
import ColorSelector from '../ColorSelector';
import OptionSelector from '../OptionSelector';
import SegmentedControl from '../SegmentedControl';
import useConfigShare from '../useConfigShare';
import ShareModal from '../ShareModal';

// --- IMPORTS FOR REUSABLE COMPONENTS ---
import DisclaimerBox from '../DisclaimerBox'; 
import CollapsibleSection from '../CollapsibleSection';

// Import Global Settings
import { 
  ENABLE_RUSH_SHIPPING, 
  RUSH_PRICE, 
  GLOBAL_DISCOUNT_PERCENT, 
  SALE_NAME,
  QS_DAYS, 
  RUSH_DAYS, 
  CUSTOM_DAYS,  
  QS_TIMELINE_TEXT, 
  RUSH_TIMELINE_TEXT, 
  CUSTOM_TIMELINE_TEXT,
  // Lead time labels + colors
  QS_LABEL, QS_COLOR, RUSH_COLOR, leadTimeTint,
  optionLine,
} from '../config/globalSettings';

// Import Data
import { holsterData } from '../data/daishoData'; 
import '../App.css';

function DaishoBuilder() {
  // --- STATE ---
  const [viewSide, setViewSide] = useState("front"); 
  const [handId, setHandId] = useState("right");
  const [brandId, setBrandId] = useState(""); 
  const [gunId, setGunId] = useState("");
  const [isLightbearing, setIsLightbearing] = useState("no"); 
  const [lightId, setLightId] = useState("");
  const [colorId, setColorId] = useState("");
  const [mountId, setMountId] = useState("");
  
  // Daisho Specific
  const [wingId, setWingId] = useState("");
  const [clawId, setClawId] = useState("black"); // Defaults to black
  
  const [breakthroughId, setBreakthroughId] = useState("");
  const [rushService, setRushService] = useState(false);

  // --- SHARE FUNCTIONALITY ---
  const {
    getConfigFromURL,
    generateShareableURL,
    saveToLocalStorage,
    loadFromLocalStorage,
    savedConfigs,
    saveNamedConfig,
    deleteNamedConfig,
    copyToClipboard,
    copySuccess,
    shareModalOpen,
    setShareModalOpen,
  } = useConfigShare();

  const getCurrentConfig = () => ({
    handId, brandId, gunId, isLightbearing, lightId, colorId, mountId, 
    wingId, clawId, breakthroughId, rushService
  });

  const loadConfig = (config) => {
    if (!config) return;
    setHandId(config.handId || 'right');
    setBrandId(config.brandId || '');
    setGunId(config.gunId || '');
    setIsLightbearing(config.isLightbearing || 'no');
    setLightId(config.lightId || '');
    setColorId(config.colorId || '');
    setMountId(config.mountId || '');
    setWingId(config.wingId || '');
    setClawId(config.clawId || 'black');
    setBreakthroughId(config.breakthroughId || '');
    setRushService(config.rushService || false);
  };

  useEffect(() => {
    const urlConfig = getConfigFromURL();
    if (urlConfig) {
      loadConfig(urlConfig);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    const savedConfig = loadFromLocalStorage();
    if (savedConfig) { loadConfig(savedConfig); }
  }, []);

  useEffect(() => {
    if (gunId) saveToLocalStorage(getCurrentConfig());
  }, [handId, brandId, gunId, isLightbearing, lightId, colorId, mountId, wingId, clawId, breakthroughId, rushService]);

  const shareableURL = generateShareableURL(getCurrentConfig());

  // --- FILTERING LOGIC ---
  const availableBrands = holsterData.brands.filter(brand => {
      return holsterData.guns.some(g => {
          if (g.brand_id !== brand.id) return false;
          if (handId === "left" && !g.qs_left) return false;
          if (isLightbearing === "yes") {
              if (!g.supported_lights || g.supported_lights.length === 0) return false;
          }
          return true;
      });
  });

  const availableGuns = brandId 
    ? holsterData.guns.filter(g => {
        if (g.brand_id !== brandId) return false;
        if (handId === "left" && !g.qs_left) return false;
        if (isLightbearing === "yes") {
            if (!g.supported_lights || g.supported_lights.length === 0) return false;
        }
        return true;
    })
    : [];

  const availableLights = isLightbearing === "yes" && gunId
    ? holsterData.lights.filter(light => {
        const gun = holsterData.guns.find(g => g.id === gunId);
        
        if (handId === 'left' && gun?.supported_lights_lh) {
            return gun.supported_lights_lh.includes(light.id);
        }
        
        return gun?.supported_lights?.includes(light.id);
      })
    : [];

  // --- AUTO-RESET LOGIC ---
  useEffect(() => {
      if (brandId && !availableBrands.find(b => b.id === brandId)) {
          setBrandId("");
      }
  }, [handId, isLightbearing, availableBrands, brandId]);

  useEffect(() => {
      if (gunId && !availableGuns.find(g => g.id === gunId)) {
          setGunId("");
      }
  }, [handId, isLightbearing, availableGuns, gunId]);

  useEffect(() => {
    if (lightId && !availableLights.find(l => l.id === lightId)) {
        setLightId("");
    }
  }, [gunId, handId, availableLights]);

  // --- HELPERS ---
  const selectedGun = holsterData.guns.find(g => g.id === gunId);
  const selectedColor = holsterData.colors.find(c => c.id === colorId);
  const selectedMount = holsterData.mounts.find(m => m.id === mountId);
  const selectedLight = holsterData.lights.find(l => l.id === lightId);
  const selectedWing = holsterData.wing_colors?.find(w => w.id === wingId);
  const selectedClaw = holsterData.claws?.find(c => c.id === clawId);
  const selectedBreakthrough = holsterData.breakthrough.find(b => b.id === breakthroughId);

  // --- PRICING ---
  const BASE_PRICE = 129.99;
  const LIGHTBEARING_UPCHARGE = 10.00;
  
  const subtotal = 
    BASE_PRICE +
    (isLightbearing === "yes" ? LIGHTBEARING_UPCHARGE : 0) +
    (selectedColor?.price || 0) +
    (selectedMount?.price || 0) +
    (selectedWing?.price || 0) +
    (selectedClaw?.price || 0) +
    (selectedBreakthrough?.price || 0) +
    (rushService && ENABLE_RUSH_SHIPPING ? RUSH_PRICE : 0);

  const discountAmount = subtotal * GLOBAL_DISCOUNT_PERCENT;
  const total = (subtotal - discountAmount).toFixed(2);

  // --- LEAD TIME (PULLED FROM GLOBAL SETTINGS) ---
  const calculateLeadTime = () => {
    if (!selectedGun) return { type: "PENDING", text: "Select Options" };
    
    let targetDays = QS_DAYS;
    if (rushService) {
        targetDays = RUSH_DAYS;
    }
    
    const date = new Date();
    let daysAdded = 0;
    while (daysAdded < targetDays) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) daysAdded++;
    }
    const dateString = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    
    if (rushService && ENABLE_RUSH_SHIPPING) {
        return {
            type: "RUSH PRIORITY",
            timeline: RUSH_TIMELINE_TEXT,
            date: dateString,
            color: RUSH_COLOR, bg: leadTimeTint(RUSH_COLOR),
            isCustom: true 
        };
    }

    return {
      type: QS_LABEL,
      timeline: QS_TIMELINE_TEXT,
      date: dateString,
      color: QS_COLOR, bg: leadTimeTint(QS_COLOR), 
      isCustom: false 
    };
  };

  const leadTimeInfo = calculateLeadTime();

  useEffect(() => {
    if (rushService && (!leadTimeInfo.isCustom || leadTimeInfo.type === QS_LABEL)) {
        setRushService(false);
    }
  }, [gunId, handId, lightId, isLightbearing]);
  
  // Validation
  const isReady = 
    brandId && 
    gunId && 
    colorId && 
    mountId && 
    wingId && 
    clawId && 
    breakthroughId && 
    (isLightbearing === "no" || (isLightbearing === "yes" && lightId));

  // --- ADD TO CART FUNCTION ---
  const handleAddToCart = () => {
    if (!isReady) return;

    const configuration = [
        `Gun Model: ${selectedGun?.name}`,
        `Hand: ${handId === 'right' ? 'Right' : 'Left'}`,
        isLightbearing === "yes" ? `Weapon Light: ${selectedLight?.name}` : "Lightbearing: No",
        `Color: ${selectedColor?.name}`,
        `Mount: ${selectedMount?.name}`,
        `Wing Color: ${selectedWing?.name}`, 
        `Claw Color: ${selectedClaw?.name}`,
        optionLine("Breakthrough Clean", selectedBreakthrough?.name),
        `Warranty: Free Lifetime Warranty`,
        `Lead Time: ${leadTimeInfo.timeline}`,
    ].filter(Boolean).join('\n');

    const productID = 283911; // LIVE DAISHO PRODUCT ID
    
    const params = new URLSearchParams();
    params.append('add-to-cart', productID);
    params.append('quantity', '1');
    params.append('anr_config', configuration);
    params.append('anr_price', total);

    const addToCartURL = `/checkout/?${params.toString()}`;
    window.location.href = addToCartURL;
  };

  const SectionHeader = ({ title, description }) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px" }}>
        <h3 style={{ textTransform: "uppercase", margin: 0 }}>{title}</h3>
        {description && (
            <div className="tooltip-wrapper">
                <span className="info-icon">i</span>
                <div className="tooltip-content">{description}</div>
            </div>
        )}
    </div>
  );

  return (
    <div className={`builder-container ${colorId ? "sticky-active" : ""}`}>
      <div className="controls-section">
        <h1 className="header-title">The Daishō Combo<br/><span style={{fontSize:"16px", color:"#D5A678"}}>AIWB HOLSTER & MAG CARRIER COMBO</span></h1>
        
        {/* FEATURES */}
        <div style={{ color: "#aaa", fontSize: "14px", lineHeight: "1.7", marginBottom: "40px" }}>
           <h3 style={{ border: "none", padding: 0, color: "#fff", textTransform: "uppercase" }}>Features</h3>
           <ul style={{ paddingLeft: "20px", marginBottom: "20px", listStyleType: "disc" }}>
             <li><strong style={{ color: "#fff" }}>Accessory Attachment:</strong> The Curv composite bridge wing connects the holster to a mag carrier, knife sheath, tourniquet holder, or other tactical accessories.</li>
             <li><strong style={{ color: "#fff" }}>Fixed Geometry:</strong> Wing and accessory positions are set during manufacture for a consistent, repeatable draw.</li>
             <li><strong style={{ color: "#fff" }}>Premium Materials:</strong> Precision-formed KYDEX® and Curv composite.</li>
             <li><strong style={{ color: "#fff" }}>Optic Ready:</strong> Compatible with a threaded barrel and suppressor-height sights up to 0.450″.</li>
           </ul>
        </div>

        {/* INPUTS */}
        <SegmentedControl 
          label="Dominant Hand" 
          options={holsterData.hands} 
          selectedValue={handId} 
          onChange={setHandId} 
        />
        
        <SegmentedControl 
          label="Weapon Light" 
          options={holsterData.lightbearing_options.map(opt => 
            opt.id === "yes" 
              ? { ...opt, name: `${opt.name} (+$${LIGHTBEARING_UPCHARGE.toFixed(2)})` } 
              : opt
          )} 
          selectedValue={isLightbearing} 
          onChange={setIsLightbearing} 
        />
        
        <OptionSelector label="Select Manufacturer *" options={availableBrands} selectedValue={brandId} onChange={(val) => { setBrandId(val); setGunId(""); }} />
        <OptionSelector label="Gun Model *" options={availableGuns} selectedValue={gunId} onChange={setGunId} disabled={!brandId} />
        
        {isLightbearing === "yes" && <OptionSelector label="Select Weapon Light *" options={availableLights} selectedValue={lightId} onChange={setLightId} />}

        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>
        
        {/* COLORS WITH TOOLTIP */}
        <SectionHeader 
            title="Color Options" 
            description="ANR Design does not have control over the pattern. The model portrayed in the configurator is a representation." 
        />
        <ColorSelector options={holsterData.colors} selectedValue={colorId} onChange={setColorId} />
        
        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>

        {/* MOUNTING WITH MEGA TOOLTIP */}
        <SectionHeader 
            title="Mounting Options" 
            description={
                <div className="mega-tooltip">
                    {holsterData.mounts.map(mount => (
                        <div key={mount.id} className="mount-info-row" style={{marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #555'}}>
                            <div className="mount-text">
                                <h4 style={{marginTop: 0, marginBottom: '5px'}}>{mount.name}</h4>
                                <p style={{margin: 0}}>{mount.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            } 
        />
        <OptionSelector label="Select Clip *" options={holsterData.mounts} selectedValue={mountId} onChange={setMountId} />

        {/* WING COLOR SELECTION WITH TOOLTIP */}
        <div style={{ marginTop: "30px" }}>
            <SectionHeader 
                title="Curv Wing Color Selection" 
                description="Select the color for the Curv composite bridge wing. The wing carries a magazine carrier, knife sheath, tourniquet holder, or other tactical accessory alongside your holster in a fixed, factory-set position." 
            />
        </div>
        <OptionSelector 
            label="Choose Wing Color *" 
            options={holsterData.wing_colors} 
            selectedValue={wingId} 
            onChange={setWingId} 
        />

        {/* CLAW COLOR SELECTION WITH TOOLTIP */}
        <div style={{ marginTop: "30px" }}>
            <SectionHeader 
                title="Claw Color Selection" 
                description="Select the color for the concealment claw attached to the holster." 
            />
        </div>
        <OptionSelector 
            label="Choose Claw Color *" 
            options={holsterData.claws} 
            selectedValue={clawId} 
            onChange={setClawId} 
        />

        {/* --- FREE LIFETIME WARRANTY BANNER --- */}
        <div style={{ marginTop: "30px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                <h3 style={{ textTransform: "uppercase", margin: 0 }}>Lifetime Warranty</h3>
                <div className="tooltip-wrapper">
                    <span className="info-icon">i</span>
                    <div className="tooltip-content">
                        Every ANR Design holster is backed by an automatic Lifetime Warranty on the Kydex® shell for the original purchaser. If your shell cracks or breaks under normal, intended use, we replace it. Consumables (hardware, loops, spacers), cosmetic wear, and damage from modifications or extreme heat are not covered. Return shipping fees apply.
                        <br/><br/>
                        <a href="https://www.anrkydexholsters.com/warranty-privacy-and-shipping-policy/" target="_blank" rel="noopener noreferrer" style={{ color: "#D5A678", textDecoration: "underline", fontWeight: "bold" }}>
                            Learn More & File a Claim
                        </a>
                    </div>
                </div>
            </div>
            <div style={{
                padding: "15px",
                backgroundColor: "rgba(76, 175, 80, 0.1)",
                borderLeft: "4px solid #4caf50",
                borderRadius: "4px",
                fontSize: "14px",
                color: "#4caf50",
                fontWeight: "600"
            }}>
                ✓ Free Lifetime Warranty Included
            </div>
        </div>

        {/* BREAKTHROUGH */}
        <div style={{ marginTop: "30px", marginBottom: "15px" }}>
           <h3 style={{ textTransform: "uppercase", margin: 0 }}>Add Breakthrough Clean</h3>
           <div style={{ 
               backgroundColor: "#1e1e1e", 
               borderLeft: "2px solid #aaa", 
               padding: "10px", 
               marginTop: "10px", 
               fontSize: "13px", 
               color: "#ccc", 
               lineHeight: "1.5" 
           }}>
             <strong>Optional Add-On:</strong> Premium firearm cleaning solvents and lubricants. <br/>
             <em>Not required for the holster itself.</em>
           </div>
        </div>
        <OptionSelector label="Optional firearm maintenance" options={holsterData.breakthrough} selectedValue={breakthroughId} onChange={setBreakthroughId} />

        <hr style={{margin:"50px 0 20px 0", borderTop:"1px solid #333"}}/>

        {/* COLLAPSIBLE DESCRIPTION */}
        <CollapsibleSection title="Description">
            <p style={{ marginTop: 0 }}>
                Carry your pistol and your essentials together with the <strong>Daishō</strong>. 
                This system pairs a precision-formed KYDEX® holster with a Curv composite bridge wing. 
                The wing carries a magazine carrier, knife sheath, tourniquet holder, or other 
                everyday carry accessory in a fixed, factory-set position.
            </p>
            <p style={{ color: "#D5A678", fontWeight: "bold", marginBottom: 0 }}>
                ALL HOLSTERS ARE OPTIC READY AND COMPATIBLE WITH A THREADED BARREL AND SUPPRESSOR-HEIGHT SIGHTS UP TO 0.450″.
            </p>
        </CollapsibleSection>

        {/* COLLAPSIBLE WARNINGS */}
        <div className="mobile-only-content">
            <CollapsibleSection title="Important Notes & Warnings">
                <DisclaimerBox mode="mobile" setShareModalOpen={setShareModalOpen} gunId={gunId} />
            </CollapsibleSection>
        </div>

      </div>

      {/* PREVIEW SECTION */}
      <div className="preview-section">
        <div className="sticky-wrapper">
          <HolsterVisualizer 
            holsterType="daisho" 
            gunData={selectedGun} 
            lightData={selectedLight}
            colorData={selectedColor} 
            mountData={selectedMount}   
            wingData={selectedWing} 
            clawData={selectedClaw}
            isLightbearing={isLightbearing} 
            isLeftHanded={handId === "left"} 
            viewSide={viewSide} 
          />

          {/* FRONT / REAR TOGGLE */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "15px", marginBottom: "15px" }}>
            <div style={{ display: "flex", backgroundColor: "#333", borderRadius: "4px", padding: "4px", gap: "5px" }}>
              <button onClick={() => setViewSide("front")} style={{ background: viewSide === "front" ? "#D5A678" : "transparent", color: viewSide === "front" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>FRONT</button>
              <button onClick={() => setViewSide("back")} style={{ background: viewSide === "back" ? "#D5A678" : "transparent", color: viewSide === "back" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>REAR</button>
            </div>
          </div>

          {/* LEAD TIME BOX */}
          {leadTimeInfo.type !== "PENDING" && (
            <div className="lead-time-box" style={{ 
                backgroundColor: leadTimeInfo.bg, 
                padding: "15px", 
                borderLeft: `4px solid ${leadTimeInfo.color}`, 
                marginBottom: "20px"
            }}>
               <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                   <div>
                       {leadTimeInfo.type && <strong style={{ color: leadTimeInfo.color, textTransform: "uppercase", fontSize: "16px" }}>{leadTimeInfo.type}</strong>}
                       <div style={{ marginTop: "5px", color: "#fff" }}>
                       Lead Time: <strong style={{ color: leadTimeInfo.color }}>{leadTimeInfo.timeline}</strong><br/>
                       Est. Ship Date: <strong>{leadTimeInfo.date}</strong>
                       </div>
                   </div>

                   {/* RUSH TOGGLE */}
                   {leadTimeInfo.isCustom && ENABLE_RUSH_SHIPPING && (
                       <div style={{ marginLeft: "15px" }}>
                           <label style={{ 
                               display: "flex", alignItems: "center", cursor: "pointer",
                               background: rushService ? "#D5A678" : "transparent",
                               border: "1px solid #d32f2f", padding: "12px 20px", borderRadius: "4px",
                               transition: "all 0.2s"
                           }}>
                               <input 
                                   type="checkbox" 
                                   checked={rushService} 
                                   onChange={(e) => setRushService(e.target.checked)}
                                   style={{ marginRight: "8px", accentColor: "#000" }} 
                               />
                               <div style={{ lineHeight: "1.7" }}>
                                   <div style={{ fontSize: "15px", fontWeight: "bold", color: rushService ? "#000" : "#D5A678", textTransform: "uppercase" }}>RUSH MY BUILD</div>
                                   <div style={{ fontSize: "15px", color: rushService ? "#000" : "#ccc" }}>+${RUSH_PRICE.toFixed(2)}</div>
                               </div>
                           </label>
                       </div>
                   )}
               </div>
            </div>
          )}
          {leadTimeInfo.type === "PENDING" && (
            <div style={{ textAlign: "center", marginTop: "15px", color: "#666", fontSize: "12px", marginBottom: "15px" }}>Select options to see lead time</div>
          )}
          
          <div className="price-tag">
            {GLOBAL_DISCOUNT_PERCENT > 0 ? (
                <>
                <span style={{ textDecoration: "line-through", color: "#888", marginRight: "10px", fontSize: "0.8em" }}>
                    ${subtotal.toFixed(2)}
                </span>
                <span style={{ color: "#00ff00" }}>
                    ${total}
                </span>
                <div style={{ fontSize: "10px", color: "#00ff00", textTransform: "uppercase", letterSpacing: "1px", marginTop: "5px" }}>
                    {SALE_NAME} ACTIVE
                </div>
                </>
            ) : (
                `Total: $${total}`
            )}
          </div>

          {/* DESKTOP DISCLAIMERS */}
          <DisclaimerBox mode="desktop" setShareModalOpen={setShareModalOpen} gunId={gunId} />

          <button className={`add-to-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>
            {isReady ? "ADD TO CART" : "SELECT OPTIONS"}
          </button>
        </div>
      </div>
      
      {/* MOBILE BAR */}
      <div className="mobile-action-bar mobile-only-content">
        <div className="mobile-price-container">
          <span className="mobile-label">Estimated Total</span>
          <span className="mobile-total">${total}</span>
        </div>
        <button 
          className={`mobile-cart-btn ${isReady ? 'pulse-active' : ''}`} 
          disabled={!isReady} 
          onClick={handleAddToCart}
        >
           {isReady ? "ADD TO CART" : "INCOMPLETE"}
        </button>
      </div>

      <ShareModal 
        isOpen={shareModalOpen} 
        onClose={() => setShareModalOpen(false)} 
        shareableURL={shareableURL} 
        currentConfig={getCurrentConfig()} 
        savedConfigs={savedConfigs} 
        onSaveConfig={saveNamedConfig} 
        loadConfig={loadConfig} 
        deleteNamedConfig={deleteNamedConfig} 
        copyToClipboard={copyToClipboard} 
        copySuccess={copySuccess} 
        gunName={selectedGun?.name} 
        lightName={selectedLight?.name}
        colorName={selectedColor?.name} 
      />
    </div>
  );
}

export default DaishoBuilder;